"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { usePathname } from "next/navigation";
import type { DoshaProductsResponse } from "@/lib/api";
import { getRemediesForDosha } from "@/lib/api";
import { withShopifyAttribution } from "@/lib/shopify-attribution";

interface RemedyProductsProps {
  doshaId: string;
  title?: string;
}

export default function RemedyProducts({
  doshaId,
  title = "Recommended Remedies from Ishvaram",
}: RemedyProductsProps) {
  const [data, setData] = useState<DoshaProductsResponse | null>(null);
  const [error, setError] = useState(false);
  const sourcePath = usePathname() || "/";

  useEffect(() => {
    getRemediesForDosha(doshaId)
      .then(setData)
      .catch(() => setError(true));
  }, [doshaId]);

  if (error || !data || data.products.length === 0) return null;

  return (
    <div className="card-sacred p-6 sm:p-8 mb-6">
      <h2
        className="text-[18px] font-bold mb-2"
        style={{
          fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
          color: "var(--ish-dark)",
        }}
      >
        {title}
      </h2>
      <p className="text-[13px] mb-5" style={{ color: "var(--ish-text-muted)" }}>
        Authentic, Siddh (energized) products for spiritual practice and dosha remediation.
      </p>

      <div className="space-y-4">
        {data.products.map((product) => (
          <a
            key={product.sku}
            href={withShopifyAttribution(product.shopify_url, sourcePath, {
              offer: "product",
              placement: "remedy_products",
              problem: doshaId,
            })}
            target="_blank"
            rel="noopener noreferrer"
            className="flex gap-4 rounded-lg p-4 transition-all duration-200 group"
            style={{
              border: "1px solid var(--ish-border)",
            }}
          >
            {/* Product Image */}
            {product.image_url && (
              <div className="flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden bg-gray-50">
                <Image
                  src={product.image_url}
                  alt={product.product_name}
                  width={80}
                  height={80}
                  sizes="80px"
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Product Info */}
            <div className="flex-1 min-w-0">
              <h3
                className="text-[14px] font-bold group-hover:text-[var(--ish-accent)] transition-colors"
                style={{ color: "var(--ish-dark)" }}
              >
                {product.product_name}
              </h3>
              <p
                className="text-[12px] mt-1 line-clamp-2"
                style={{ color: "var(--ish-text-muted)" }}
              >
                {product.reason}
              </p>
              <div className="flex items-center gap-3 mt-2">
                {product.current_price && (
                  <span className="text-[14px] font-bold" style={{ color: "var(--ish-accent)" }}>
                    ₹{product.current_price}
                  </span>
                )}
                {!product.current_price && product.price_range && (
                  <span
                    className="text-[13px] font-semibold"
                    style={{ color: "var(--ish-accent)" }}
                  >
                    {product.price_range}
                  </span>
                )}
                <span
                  className="text-[11px] px-2 py-0.5 rounded"
                  style={{
                    backgroundColor: "var(--ish-accent-light)",
                    color: "var(--ish-accent)",
                  }}
                >
                  Siddh (Energized)
                </span>
              </div>
            </div>

            {/* Arrow */}
            <div
              className="flex-shrink-0 flex items-center text-[16px] opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ color: "var(--ish-accent)" }}
            >
              &rarr;
            </div>
          </a>
        ))}
      </div>

      {/* Trust Signals */}
      <div
        className="flex flex-wrap gap-4 mt-5 pt-4 text-[11px]"
        style={{
          borderTop: "1px solid var(--ish-border)",
          color: "var(--ish-text-faint)",
        }}
      >
        <span>4.8&#9733; Average Rating</span>
        <span>Free Shipping on &#8377;499+</span>
        <span>Ethically Sourced</span>
      </div>
    </div>
  );
}
